import { useState, type ReactNode } from 'react';
import { findPort, rateValidity } from '@hana/domain';
import { useT } from '@hana/i18n';
import { useToast } from '@hana/ui';
import {
  IconArrowLeft, IconArrowRight, IconCalendarEvent, IconChecklist, IconChevronDown, IconChevronRight, IconFileText, IconFlag, IconFlag3, IconLink, IconMap2,
  IconShip, IconSearch, IconDots, IconMessage2, IconExternalLink, IconTag, IconUserCircle, IconChartBar, IconMessage, IconBell,
} from '@tabler/icons-react';
import { REGION_GROUPS, type CellStatus } from './costRates';
import { BOARD_TODAY } from './fixtures';
import { servesLane, PICS, type LaneRow } from './services';
import { BOARD_LTS } from './servicesBoardData';
import { LTS_WEEKS, cellView } from './lts';
import { LAND_PATH, PORT_LL } from './worldMap';
import { isVnPort } from './svcmap';
import type { CostRate, Service } from './types';
import { viNum } from './format';
import s from './svcdetail.module.css';
import './LaneDetail.i18n';
import m from './lanedetail.module.css';

const SVCOL = ['var(--pri)', 'var(--warn)', 'var(--ok)', 'var(--link)', 'var(--err)', 'var(--tx2)', 'var(--lb)', 'var(--tx)'];
const STATUS_TX: Record<CellStatus, string> = { ok: 'ld.stOk', soon: 'ld.stSoon', exp: 'ld.stExp', none: 'ld.stNone' };
const REMIND = [3, 7, 14];
const MAP_H = 380;
const MAP_W = 760;

interface Todo { k: string; sub: string; tone: 'warn' | 'err' | 'lb' | 'ok'; act?: string; go?: () => void }
interface Leg { d: string; ar: string; col: string; dash: boolean; on: boolean; sel: number }
interface RowSvc { svc: Service; col: string; path: string[]; hub: string; tt: number }

function Acc({ id, open, toggle, icon, title, tip, sum, children }: { id: string; open: boolean; toggle: (id: string) => void; icon: ReactNode; title: string; tip: string; sum: string; children: ReactNode }) {
  const t = useT();
  return (
    <section className={s.card} aria-label={title}>
      <div className={s.cardHead}>
        <span className={s.cardIco} aria-hidden="true">{icon}</span>
        <h2 className={s.h2} title={tip}>{title}</h2>
        <button type="button" className={s.fold} aria-expanded={open} aria-label={t('ld.fold', { v: title.toLowerCase() })} title={t('ld.foldTip')} onClick={() => toggle(id)}>
          <IconChevronDown size={18} aria-hidden="true" className={open ? s.up : undefined} />
        </button>
      </div>
      {open ? children : <div className={s.sum}>{sum}</div>}
    </section>
  );
}

const nm = (c: string) => findPort(c)?.name ?? c;
const groupName = (g: LaneRow['group']) => REGION_GROUPS.find((x) => x.key === g)?.label ?? g;

/** Đường đi của service trên tuyến: phần rotation từ POL tới POD, hoặc POL → cảng chuyển tải → POD. */
function pathOf(svc: Service, lane: LaneRow, hub: string): string[] {
  const rot = svc.rotation.filter((p, i, a) => p !== a[i - 1]);
  const a = rot.indexOf(lane.pol);
  const b = a >= 0 ? rot.indexOf(lane.pod, a + 1) : -1;
  const p = b > a ? rot.slice(a, b + 1) : [lane.pol, ...(hub ? [hub] : []), lane.pod];
  return p.filter((c) => PORT_LL[c]);
}

/** Trang chi tiết tuyến (Mint-ServicesMap `vRt`): trái Bản đồ · Hãng & service · Lịch tàu · Hoạt động; phải Việc cần làm · Thông tin chính · Giá hiện có · Người phụ trách · Liên kết. */
export function LaneDetail({ lane, services, rates, served, follow, onFollow, pic, onPic, onBack }: {
  lane: LaneRow;
  services: readonly Service[];
  rates: readonly CostRate[];
  served: readonly string[];
  follow: boolean;
  onFollow: () => void;
  pic: string;
  onPic: (p: string) => void;
  onBack: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const [shut, setShut] = useState<ReadonlySet<string>>(new Set(['rlnk']));
  const [rem, setRem] = useState(7);
  const [menu, setMenu] = useState(false);
  const [selSvc, setSelSvc] = useState<string | null>(null);
  const toggle = (id: string) => setShut((c) => { const n = new Set(c); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const isOpen = (id: string) => !shut.has(id);
  const pending = () => toast({ msg: t('ld.noWrite'), tone: 'err' });

  const direct = !lane.viaOnly;
  const laneRates = rates.filter((r) => r.pol === lane.pol && r.pod === lane.pod);
  const hubs = [...new Set(laneRates.map((r) => r.via).filter((v): v is string => !!v))];
  const serving = services.filter((x) => servesLane(x, lane.pol, lane.pod));

  // service trên tuyến: service đi thẳng theo rotation; thiếu thì dựng từ hãng + cảng chuyển tải
  const rowsSvc: RowSvc[] = serving.length
    ? serving.map((svc, k) => ({ svc, col: SVCOL[k % SVCOL.length] as string, path: pathOf(svc, lane, ''), hub: '', tt: lane.tt ?? 0 }))
    : served.slice(0, 4).map((c, k) => {
        const hub = hubs[k % Math.max(hubs.length, 1)] ?? '';
        const svc: Service = { id: `${c} ${hub ? `via ${hub}` : 'direct'}`, name: hub ? t('ld.svcHub') : t('ld.svcNone'), carriers: [c], rotation: [lane.pol, lane.pod], circuitDays: 0, ships: 0, freqPerWeek: 1, locked: false };
        return { svc, col: SVCOL[k % SVCOL.length] as string, path: pathOf(svc, lane, hub), hub, tt: (lane.tt ?? 0) + k * 2 };
      });
  const sel = selSvc && rowsSvc.some((x) => x.svc.id === selSvc) ? selSvc : null;
  const pick = (id: string) => setSelSvc(sel === id ? null : id);

  // ----- bản đồ: equirectangular theo khung đường bờ 1000×420
  const BX = (lon: number) => ((lon + 180) / 360) * 1000;
  const BY = (lat: number) => ((83 - lat) / 139) * 420;
  const uw = new Map<string, number>();
  const unwrap = (c: string, prev?: number) => {
    if (uw.has(c)) return;
    let lon = PORT_LL[c]![0];
    if (prev !== undefined) { while (lon - prev > 180) lon -= 360; while (prev - lon > 180) lon += 360; }
    uw.set(c, lon);
  };
  if (PORT_LL[lane.pol]) uw.set(lane.pol, PORT_LL[lane.pol]![0]);
  for (const x of rowsSvc) { let prev = uw.get(lane.pol); for (const c of x.path) { unwrap(c, prev); prev = uw.get(c); } }
  if (PORT_LL[lane.pod]) unwrap(lane.pod, uw.get(lane.pol));
  const codes = [...uw.keys()];
  const none = codes.length < 2;
  let tf = '';
  let lands: number[] = [];
  const legs: Leg[] = [];
  const chips: { id: string; x: number; y: number; col: string; on: boolean }[] = [];
  const ports: { c: string; x: number; y: number; main: boolean; hub: boolean; vn: boolean }[] = [];
  const calls: { x: number; y: number; col: string }[] = [];
  if (!none) {
    const xs = codes.map((c) => BX(uw.get(c)!));
    const ys = codes.map((c) => BY(PORT_LL[c]![1]));
    const x0 = Math.min(...xs), x1 = Math.max(...xs), y0 = Math.min(...ys), y1 = Math.max(...ys);
    const spX = Math.max(x1 - x0, 30), spY = Math.max(y1 - y0, 18), aw = MAP_W - 160, ah = MAP_H - 124;
    const sc = Math.min(aw / spX, ah / spY, 60);
    const tx = 80 + aw / 2 - ((x0 + x1) / 2) * sc, ty = 64 + ah / 2 - ((y0 + y1) / 2) * sc;
    const P = (c: string): [number, number] => [BX(uw.get(c)!) * sc + tx, BY(PORT_LL[c]![1]) * sc + ty];
    tf = `translate(${tx.toFixed(1)} ${ty.toFixed(1)}) scale(${sc.toFixed(4)})`;
    const vx0 = -tx / sc, vx1 = (MAP_W - tx) / sc;
    lands = [-1, 0, 1].filter((k) => k * 1000 < vx1 && k * 1000 + 1000 > vx0);
    const n = rowsSvc.length;
    rowsSvc.forEach((x, k) => {
      const on = sel === x.svc.id;
      const fan = (k - (n - 1) / 2) * 16;
      let best: { len: number; at: [number, number] } | null = null;
      for (let j = 1; j < x.path.length; j++) {
        const A = P(x.path[j - 1]!), B = P(x.path[j]!);
        const dx = B[0] - A[0], dy = B[1] - A[1], len = Math.hypot(dx, dy);
        if (len < 2) continue;
        const off = Math.min(Math.max(len * 0.16, 10), 64) + fan;
        const C: [number, number] = [(A[0] + B[0]) / 2 + (-dy / len) * off, (A[1] + B[1]) / 2 + (dx / len) * off];
        let ar = '';
        if (j === x.path.length - 1) {
          const u = 0.62;
          const M: [number, number] = [(1 - u) * (1 - u) * A[0] + 2 * u * (1 - u) * C[0] + u * u * B[0], (1 - u) * (1 - u) * A[1] + 2 * u * (1 - u) * C[1] + u * u * B[1]];
          const gx = 2 * (1 - u) * (C[0] - A[0]) + 2 * u * (B[0] - C[0]), gy = 2 * (1 - u) * (C[1] - A[1]) + 2 * u * (B[1] - C[1]);
          const gl = Math.hypot(gx, gy) || 1, ux = gx / gl, uy = gy / gl;
          ar = `M${(M[0] + ux * 6).toFixed(1)} ${(M[1] + uy * 6).toFixed(1)}L${(M[0] - ux * 5 - uy * 5).toFixed(1)} ${(M[1] - uy * 5 + ux * 5).toFixed(1)}L${(M[0] - ux * 5 + uy * 5).toFixed(1)} ${(M[1] - uy * 5 - ux * 5).toFixed(1)}Z`;
        }
        legs.push({ d: `M${A[0].toFixed(1)} ${A[1].toFixed(1)}Q${C[0].toFixed(1)} ${C[1].toFixed(1)} ${B[0].toFixed(1)} ${B[1].toFixed(1)}`, ar, col: x.col, dash: !!x.hub, on, sel: on ? 1 : 0 });
        if (!best || len > best.len) {
          const u = 0.5 + (k - (n - 1) / 2) * 0.09;
          best = { len, at: [(1 - u) * (1 - u) * A[0] + 2 * u * (1 - u) * C[0] + u * u * B[0], (1 - u) * (1 - u) * A[1] + 2 * u * (1 - u) * C[1] + u * u * B[1]] };
        }
      }
      if (best) chips.push({ id: x.svc.id, x: best.at[0], y: best.at[1], col: x.col, on });
    });
    legs.sort((a, b) => a.sel - b.sel);
    const hubSet = new Set(rowsSvc.map((x) => x.hub).filter(Boolean));
    const selPath = sel ? new Set(rowsSvc.find((x) => x.svc.id === sel)?.path ?? []) : null;
    for (const c of codes) {
      const [x, y] = P(c);
      const main = c === lane.pol || c === lane.pod, hub = hubSet.has(c);
      if (main || hub || selPath?.has(c)) ports.push({ c, x, y, main, hub, vn: isVnPort(c) });
      else calls.push({ x, y, col: rowsSvc.find((r) => r.path.includes(c))?.col ?? 'var(--tx2)' });
    }
  }

  // ----- giá hiện có theo hãng
  const carriers = [...new Set([...served, ...laneRates.map((r) => r.carrier)])];
  const priceRows = carriers.map((car) => {
    const rs = laneRates.filter((r) => r.carrier === car);
    let best: { status: CellStatus; left: number; end: string; from: string } | null = null;
    for (const r of rs) {
      const v = rateValidity({ validFrom: r.validFrom, validEnd: r.validEnd }, BOARD_TODAY);
      if (v.status === 'upcoming') continue;
      const status: CellStatus = v.status === 'expired' ? 'exp' : v.status === 'expiring' ? 'soon' : 'ok';
      if (!best || v.daysLeft > best.left) best = { status, left: v.daysLeft, end: r.validEnd, from: r.validFrom ?? '' };
    }
    return { car, ...(best ?? { status: 'none' as CellStatus, left: 0, end: '', from: '' }) };
  });
  const nOk = priceRows.filter((r) => r.status === 'ok').length, nSoon = priceRows.filter((r) => r.status === 'soon').length;
  const nExp = priceRows.filter((r) => r.status === 'exp' || r.status === 'none').length;
  const totalC = priceRows.length || 1;
  const dmFmt = (iso: string) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '—');
  const nextEnd = priceRows.filter((r) => r.status === 'soon' || r.status === 'ok').map((r) => r.end).sort()[0];

  // ----- việc cần làm
  const todo: Todo[] = [];
  if (!pic) todo.push({ k: t('ld.tAssign'), sub: t('ld.tAssignSub'), tone: 'err', act: t('ld.assign'), go: pending });
  if (nSoon + nExp > 0 && lane.total > 0) todo.push({ k: t('ld.tRenew', { n: nSoon + nExp }), sub: `${nExp ? `${t('ld.expN', { n: nExp })} · ` : ''}${nSoon ? `${t('ld.soonN', { n: nSoon })} · ` : ''}${t('ld.remindBefore', { n: rem })}`, tone: nExp ? 'err' : 'warn', act: t('ld.remind'), go: pending });
  if (lane.pending > 0) todo.push({ k: t('ld.tPending', { n: lane.pending }), sub: t('ld.tPendingSub'), tone: 'warn', act: t('ld.open'), go: () => { window.location.assign('/ezy/inquiry'); } });
  if (lane.total === 0) todo.push({ k: t('ld.tNoRate'), sub: t('ld.tNoRateSub'), tone: 'warn', act: t('ld.enter'), go: () => { window.location.assign('/ezy/rates'); } });
  if (nOk > 0) todo.push({ k: t('ld.tValid', { n: nOk }), sub: nextEnd ? t('ld.earliest', { v: dmFmt(nextEnd) }) : t('ld.enough'), tone: 'ok' });
  const nTodo = todo.filter((x) => x.tone !== 'ok').length;

  const code = `FCLDC${lane.pol}${lane.pod}`;
  const info: [string, string][] = [
    [t('ld.iCode'), code],
    [t('ld.iLane'), `${nm(lane.pol)} → ${nm(lane.pod)}`],
    [t('ld.iGroup'), groupName(lane.group)],
    [t('ld.iTs'), direct ? t('ld.direct') : hubs.join(' · ') || '—'],
    ['T/T', lane.tt ? t('ld.days', { n: lane.tt }) : '—'],
    [t('ld.iFreq'), lane.freq ? t('ld.perWeekN', { n: lane.freq }) : '—'],
    [t('ld.carrier'), carriers.length ? `${carriers.length} · ${carriers.slice(0, 5).join(' ')}` : '—'],
  ];
  const links: [string, string, string][] = [
    [t('ld.lCost'), '/ezy/rates', t('ld.open')], [t('ld.lLookup'), '/ezy', t('ld.look')], [t('ld.lInq'), '/ezy/inquiry', t('ld.open')], [t('ld.lQuote'), '/ezy/quote', t('ld.open')], [t('ld.lJob'), '/ezy/job', t('ld.open')],
  ];

  // ----- lịch tàu sắp tới: dải tuần theo service có lịch dài hạn
  const weekRows = rowsSvc.slice(0, 8).map((x) => {
    const raw = BOARD_LTS[x.svc.id];
    return { x, cells: LTS_WEEKS.slice(0, 6).map((_, i) => ({ w: LTS_WEEKS[i], c: raw ? cellView(x.svc.id, raw[i] ?? '-', i) : null })) };
  });
  const dot = t(STATUS_TX[lane.status]);
  const nTra = 20 + lane.total * 2;
  const act: [string, ReactNode, number, string, string][] = [
    [t('ld.aLook'), <IconSearch key="a" size={18} aria-hidden="true" />, nTra, t('ld.aLookTip'), '/ezy'],
    [t('ld.aInq'), <IconMessage key="b" size={18} aria-hidden="true" />, lane.pending + 2, lane.pending ? t('ld.aInqWait', { n: lane.pending }) : t('ld.aInqDone'), '/ezy/inquiry'],
    [t('ld.aQuote'), <IconTag key="c" size={18} aria-hidden="true" />, 4 + (lane.total % 9), t('ld.aQuoteTip'), '/ezy/quote'],
    ['Job', <IconShip key="d" size={18} aria-hidden="true" />, follow ? 9 : 2, t('ld.aJobTip'), '/ezy/job'],
  ];
  const maxTT = Math.max(...rowsSvc.map((x) => x.tt), 1);

  return (
    <div className={s.root}>
      <header className={s.head}>
        <button type="button" className={s.back} aria-label={t('ld.back')} title={t('ld.back')} onClick={onBack}>
          <IconArrowLeft size={17} aria-hidden="true" />
        </button>
        <h1 className={s.title} aria-label={t('ld.laneAria', { a: lane.pol, b: lane.pod })}>{lane.pol} <IconArrowRight size={20} aria-hidden="true" className={m.arrow} /> {lane.pod}</h1>
        <span className={`${m.pdot} ${m[`p_${lane.status}`]}`} role="img" aria-label={t('ld.priceDot', { d: dot, a: lane.live, b: lane.total })} title={t('ld.priceDot', { d: dot, a: lane.live, b: lane.total })} />
        <button type="button" className={m.iconBtn} aria-pressed={follow} aria-label={follow ? t('ld.unfollow') : t('ld.follow')} title={follow ? t('ld.unfollow') : t('ld.follow')} onClick={onFollow}>
          {follow ? <IconFlag3 size={18} aria-hidden="true" fill="currentColor" /> : <IconFlag size={18} aria-hidden="true" />}
        </button>
        <span className={`${m.avatar} ${pic ? '' : m.avatarNone}`} role="img" aria-label={pic ? t('ld.picIs', { v: pic }) : t('ld.picNone')} title={pic ? t('ld.picIs', { v: pic }) : t('ld.picNone')}>{pic || '+'}</span>
        <span className={s.grow} />
        <span className={s.menuWrap}>
          <button type="button" className={s.more} aria-label={t('ld.moreAct')} title={t('ld.moreAct')} aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu(!menu)}>
            <IconDots size={17} aria-hidden="true" />
          </button>
          {menu && (
            <div className={s.menu} role="menu" aria-label={t('ld.moreAct')}>
              {[t('ld.mAssign'), t('ld.mMerge'), t('ld.mExport'), t('ld.mRemind'), t('ld.mEdit')].map((k) => (
                <button key={k} type="button" role="menuitem" className={s.mi} onClick={() => { setMenu(false); pending(); }}>{k}</button>
              ))}
              <button type="button" role="menuitem" className={s.mi} onClick={() => { setMenu(false); onFollow(); }}>{follow ? t('ld.mUnfollow') : t('ld.follow')}</button>
              <button type="button" role="menuitem" className={`${s.mi} ${s.miErr}`} onClick={() => { setMenu(false); pending(); }}>{t('ld.mStop')}</button>
              <div className={s.sep} />
              <a role="menuitem" className={s.mi} href="/ezy/inquiry"><IconMessage2 size={16} aria-hidden="true" />{t('ld.mInquiry')}</a>
              <a role="menuitem" className={s.mi} href="/ezy/rates"><IconExternalLink size={16} aria-hidden="true" />{t('ld.mRates')}</a>
            </div>
          )}
        </span>
        <a className={m.linkBtn} href="/ezy"><IconSearch size={16} aria-hidden="true" />{t('ld.lLookup')}</a>
      </header>
      <div className={s.cols}>
        <div className={s.left}>
          <Acc id="rmap" open={isOpen('rmap')} toggle={toggle} icon={<IconMap2 size={18} stroke={1.9} />} title={t('ld.map')} tip={t('ld.mapTip')} sum={t('ld.mapSum', { a: lane.pol, b: lane.pod, n: rowsSvc.length })}>
            <section className={m.map} aria-label={t('ld.mapSum', { a: lane.pol, b: lane.pod, n: rowsSvc.length })}>
              <svg aria-hidden="true" width="100%" height={MAP_H} viewBox={`0 0 ${MAP_W} ${MAP_H}`} preserveAspectRatio="xMidYMid slice" className={m.svg}>
                <g transform={tf}>
                  {lands.map((k) => <path key={k} d={LAND_PATH} transform={`translate(${k * 1000} 0)`} className={m.land} vectorEffect="non-scaling-stroke" />)}
                </g>
                {legs.map((g, i) => (
                  <g key={i} style={{ color: g.col }} opacity={sel ? (g.on ? 1 : 0.28) : 0.9}>
                    <path d={g.d} fill="none" stroke="currentColor" strokeWidth={g.on ? 3.4 : 2.4} strokeDasharray={g.dash ? '7 6' : undefined} strokeLinecap="round" />
                    {g.ar && <path d={g.ar} fill="currentColor" />}
                  </g>
                ))}
                {calls.map((c, i) => <circle key={i} cx={c.x} cy={c.y} r={4} className={m.call} style={{ color: c.col }} />)}
                {ports.map((p) => (
                  <g key={p.c} role="img" aria-label={`${p.c} · ${p.c === lane.pol ? t('ld.pPol') : p.c === lane.pod ? t('ld.pPod') : p.hub ? t('ld.pHub') : t('ld.pCall')}`}>
                    {p.hub ? <rect x={p.x - 6} y={p.y - 6} width={12} height={12} rx={4} className={m.hubDot} /> : <circle cx={p.x} cy={p.y} r={p.main ? 8 : 5} className={`${m.portDot} ${p.vn ? m.portVn : ''} ${p.main ? m.portMain : ''}`} />}
                    <text x={p.x + 12} y={p.y + 4} className={`${m.portTx} ${p.vn ? m.portTxVn : ''} ${p.main ? m.portTxMain : ''}`}>{p.c}</text>
                  </g>
                ))}
              </svg>
              {chips.map((c) => (
                <button key={c.id} type="button" className={`${m.chip} ${c.on ? m.chipOn : ''}`} style={{ left: `${(c.x / MAP_W) * 100}%`, top: c.y, ['--c' as string]: c.col }} aria-pressed={c.on} aria-label={t('ld.svcBelow', { v: c.id })} title={c.id} onClick={() => pick(c.id)}>{c.id}</button>
              ))}
              {none && <div className={m.none}>{t('ld.noCoord')}</div>}
              <div className={m.stats}>
                <span>T/T <b>{lane.tt ? `${direct ? '' : '~'}${t('ld.days', { n: lane.tt })}` : '—'}</b></span>
                <span><b>{t('ld.freqWeek', { n: lane.freq || 0 })}</b></span>
                <span>{t('ld.carrier')} <b>{lane.total}</b></span>
              </div>
              <div className={m.legend}>
                <span><i className={m.lgSolid} />{t('ld.direct')}</span>
                {hubs.length > 0 && <span><i className={m.lgDash} />{t('ld.iTs')}</span>}
                <span><i className={m.lgVn} />{t('ld.vnPort')}</span>
              </div>
            </section>
          </Acc>
          <Acc id="rsvc" open={isOpen('rsvc')} toggle={toggle} icon={<IconShip size={18} stroke={1.9} />} title={t('ld.carSvc')} tip={t('ld.carSvcTip')} sum={t('ld.svcN', { n: rowsSvc.length })}>
            <div className={m.svcHead}><span className={m.cCode}>Service</span><span className={m.cCar}>{t('ld.carrier')}</span><span className={m.cTt}>{t('ld.ttDays')}</span><span className={m.cFreq}>{t('ld.perWeek')}</span><span className={m.cDot} title={t('ld.dotTip')}>ⓘ</span><span className={m.cGo} /></div>
            {rowsSvc.map((x) => (
              <div key={x.svc.id} className={`${m.svcRow} ${sel === x.svc.id ? m.svcOn : ''}`}>
                <button type="button" className={m.svcMain} aria-pressed={sel === x.svc.id} aria-label={t('ld.pickOnMap', { v: x.svc.id })} title={`${x.svc.id} · ${x.svc.name} · ${x.svc.carriers.join(' · ')}`} onClick={() => pick(x.svc.id)}>
                  <span className={m.cCode}><i className={m.sw} style={{ background: x.col }} /><b>{x.svc.id}</b></span>
                  <span className={m.cCar}><span className={m.carChip}>{x.svc.carriers[0] ?? '—'}</span>{x.svc.carriers.length > 1 && <span className={m.carMore} title={x.svc.carriers.slice(1).join(' · ')}>+{x.svc.carriers.length - 1}</span>}</span>
                  <span className={m.cTt}><span className={m.ttBar}><i style={{ width: `${Math.max(6, Math.round((x.tt / maxTT) * 100))}%` }} /></span><b>{x.tt || '—'}</b></span>
                  <span className={m.cFreq}>1</span>
                  <span className={m.cDot} title={x.hub ? t('ld.tsVia', { v: x.hub }) : t('ld.direct')}><i className={x.hub ? m.dotHub : m.dotDirect} /></span>
                </button>
                <span className={m.cGo} />
              </div>
            ))}
          </Acc>
          <Acc id="rwk" open={isOpen('rwk')} toggle={toggle} icon={<IconCalendarEvent size={18} stroke={1.9} />} title={t('ld.sched')} tip={t('ld.schedTip')} sum={t('ld.schedSum', { n: rowsSvc.length })}>
            <div className={m.wkHead}><span className={m.wkCode} />{LTS_WEEKS.slice(0, 6).map((w, i) => <span key={w[0]} className={i ? undefined : m.wkNow}>{w[0]}</span>)}</div>
            {weekRows.map(({ x, cells }) => (
              <div key={x.svc.id} className={`${m.wkRow} ${sel === x.svc.id ? m.svcOn : ''}`}>
                <button type="button" className={m.wkCode} aria-pressed={sel === x.svc.id} aria-label={t('ld.pick', { v: x.svc.id })} onClick={() => pick(x.svc.id)}><i className={m.sw} style={{ background: x.col }} /><b>{x.svc.id}</b></button>
                {cells.map(({ w, c }, i) => (
                  <a key={i} href="/ezy" className={`${m.wk} ${c ? m[`wk_${c.kind}`] : m.wk_nodata}`} aria-label={`${x.svc.id} · ${w?.[0] ?? ''} · ${c ? c.al : t('ld.noData')} · ${t('ld.openSched')}`} title={c ? c.al : t('ld.noData')}>{c ? (c.kind === 'ok' ? c.n : c.kind === 'blank' ? 'Blank' : c.kind === 'end' ? t('ld.end') : '—') : '—'}</a>
                ))}
              </div>
            ))}
          </Acc>
          <Acc id="ract" open={isOpen('ract')} toggle={toggle} icon={<IconChartBar size={18} stroke={1.9} />} title={t('ld.activity')} tip={t('ld.activityTip')} sum={act.map((a) => `${a[0]} ${a[2]}`).join(' · ')}>
            <div className={m.tiles}>
              {act.map(([k, ic, v, tip, href]) => (
                <a key={k} href={href} className={m.tile} aria-label={t('ld.in30', { tip, v })} title={t('ld.in30', { tip, v })}>{ic}<b>{v}</b><span>{k}</span></a>
              ))}
            </div>
          </Acc>
        </div>
        <div className={s.right}>
          <Acc id="rtodo" open={isOpen('rtodo')} toggle={toggle} icon={<IconChecklist size={18} stroke={1.9} />} title={t('ld.todo')} tip={t('ld.todoTip')} sum={nTodo ? t('ld.todoN', { n: nTodo }) : t('ld.noTodo')}>
            <ul className={s.list}>
              {todo.map((x) => (
                <li key={x.k}>
                  <span className={`${s.tdot} ${s[`t_${x.tone}`]}`} aria-hidden="true" />
                  <span className={s.tx}><b>{x.k}</b><span>{x.sub}</span></span>
                  {x.act && <button type="button" className={s.act} onClick={x.go}>{x.act}</button>}
                </li>
              ))}
              {todo.length === 0 && <li className={s.lbv}>{t('ld.noTodoLi')}</li>}
            </ul>
          </Acc>
          <Acc id="rinfo" open={isOpen('rinfo')} toggle={toggle} icon={<IconFileText size={18} stroke={1.9} />} title={t('ld.mainInfo')} tip={t('ld.mainInfoTip', { c: code, r: `FCLRF${lane.pol}${lane.pod}` })} sum={`${code} · ${direct ? t('ld.direct') : hubs.join(' · ')}${lane.tt ? ` · ${t('ld.days', { n: lane.tt })}` : ''}`}>
            <dl className={s.dl}>
              {info.map(([k, val]) => (
                <div key={k}><dt>{k}</dt><dd title={val}>{val}</dd></div>
              ))}
            </dl>
          </Acc>
          <Acc id="rprice" open={isOpen('rprice')} toggle={toggle} icon={<IconTag size={18} stroke={1.9} />} title={t('ld.prices')} tip={t('ld.pricesTip')} sum={t('ld.pricesSum', { a: nOk, b: nSoon, c: nExp, d: priceRows.length })}>
            <div className={m.priceBody}>
              <div className={m.seg} role="img" aria-label={t('ld.pricesSum', { a: nOk, b: nSoon, c: nExp, d: priceRows.length })}>
                <i style={{ width: `${(nOk / totalC) * 100}%`, background: 'var(--ok)' }} /><i style={{ width: `${(nSoon / totalC) * 100}%`, background: 'var(--warn)' }} /><i style={{ width: `${(nExp / totalC) * 100}%`, background: 'var(--err)' }} />
              </div>
              <div className={m.pHead}><span>{t('ld.carrier')}</span><span>{t('ld.validity')}</span><span>{t('ld.left')}</span></div>
              {priceRows.slice(0, 7).map((r) => (
                <div key={r.car} className={m.pRow} title={`${code}${r.car} · ${r.from ? t('ld.validRange', { a: dmFmt(r.from), b: dmFmt(r.end) }) : t('ld.stNone').toLowerCase()} · ${t(STATUS_TX[r.status])}`}>
                  <span className={m.pCar}><i className={`${m.pd} ${m[`p_${r.status}`]}`} /><b>{r.car}</b></span>
                  <span className={m.pBar}><i className={m[`p_${r.status}`]} style={{ width: `${Math.max(0, Math.min(100, Math.round((r.left / 45) * 100)))}%` }} /></span>
                  <b className={m.pLeft}>{r.left > 0 ? viNum(r.left) : t('ld.expiredShort')}</b>
                </div>
              ))}
              {priceRows.length > 7 && <div className={m.more}>{t('ld.moreCars', { n: priceRows.length - 7 })}</div>}
            </div>
          </Acc>
          <Acc id="rset" open={isOpen('rset')} toggle={toggle} icon={<IconUserCircle size={18} stroke={1.9} />} title={t('ld.owner')} tip={t('ld.ownerTip')} sum={t('ld.ownerSum', { p: pic || t('ld.unassigned'), f: follow ? 'Follow' : t('ld.noFollow'), n: rem })}>
            <div className={m.setRow}>
              <IconUserCircle size={16} aria-hidden="true" />
              <span title={t('ld.picTip')}>PIC</span>
              <div className={m.radios} role="radiogroup" aria-label={t('ld.picGroup')}>
                {[...PICS, ''].map((p) => (
                  <button key={p || '-'} type="button" role="radio" aria-checked={pic === p} aria-label={p ? t('ld.pricingP', { v: p }) : t('ld.unassigned')} className={pic === p ? m.radioOn : undefined} onClick={() => onPic(p)}>{p || '—'}</button>
                ))}
              </div>
            </div>
            <div className={m.setRow}>
              <IconFlag size={16} aria-hidden="true" />
              <span title={t('ld.followTip')}>Follow</span>
              <button type="button" role="switch" aria-checked={follow} aria-label={t('ld.follow')} className={`${m.sw2} ${follow ? m.sw2On : ''}`} onClick={onFollow}><i /></button>
            </div>
            <div className={m.setRow}>
              <IconBell size={16} aria-hidden="true" />
              <span title={t('ld.remindTip')}>{t('ld.remindLabel')}</span>
              <div className={m.radios} role="radiogroup" aria-label={t('ld.remindGroup')}>
                {REMIND.map((n) => (
                  <button key={n} type="button" role="radio" aria-checked={rem === n} className={rem === n ? m.radioOn : undefined} onClick={() => setRem(n)}>{t('ld.days', { n })}</button>
                ))}
              </div>
            </div>
          </Acc>
          <Acc id="rlnk" open={isOpen('rlnk')} toggle={toggle} icon={<IconLink size={18} stroke={1.9} />} title={t('ld.links')} tip={t('ld.linksTip')} sum={t('ld.linksSum', { n: links.length })}>
            <ul className={s.list}>
              {links.map(([k, href, go]) => (
                <li key={k}><a className={m.lnk} href={href}><span>{k}</span><span>{go}<IconChevronRight size={14} aria-hidden="true" /></span></a></li>
              ))}
            </ul>
          </Acc>
        </div>
      </div>
    </div>
  );
}
